import { HttpErrorResponse } from '@angular/common/http';

/**
 * Reading `error_code` off a failed chat call, and deciding what the UI owes the user for it.
 *
 * Two error shapes reach us, because `ApiService` is not consistent: a GET rethrows the whole
 * `HttpErrorResponse`, while POST/PATCH/DELETE go through its `handleError`, which throws the
 * parsed **body** instead. Everything here accepts either.
 *
 * The one code that never arrives is `CHAT_UNAUTHENTICATED`: the global `/api/v1` auth middleware
 * rejects an expired token before any chat code runs, so a 401 is a bare
 * `{ success: false, message }` handled by the existing interceptor.
 */

/** Error codes from §12 of `docs/backend/chat/internal-chat.md`. */
export type ChatErrorCode =
  | 'CHAT_INVALID_REQUEST'
  | 'CHAT_UNAUTHENTICATED'
  | 'CHAT_NOT_A_MEMBER'
  | 'CHAT_NOT_GROUP_OWNER'
  | 'CHAT_NOT_SENDER'
  | 'CHAT_OUT_OF_OFFICE'
  | 'CHAT_EXTERNAL_DISABLED'
  | 'CHAT_EDIT_WINDOW_EXPIRED'
  | 'CHAT_ATTACHMENT_NOT_YOURS'
  | 'CHAT_NOT_FOUND'
  | 'CHAT_FILE_TOO_LARGE'
  | 'CHAT_FILE_TYPE_BLOCKED'
  | 'CHAT_EMPTY_MESSAGE'
  | 'CHAT_MESSAGE_TOO_LONG'
  | 'CHAT_INVALID_STATUS'
  | 'CHAT_TOO_MANY_REACTIONS'
  | 'CHAT_RATE_LIMITED'
  | 'CHAT_SENDER_HIDDEN'
  | 'CHAT_MASTER_ADMIN_REQUIRED';

/** Spec default for a 429 when the server sends no `Retry-After` we can read. */
export const CHAT_RATE_LIMIT_FALLBACK_MS = 10_000;

/** The payload half of an error, whichever shape it arrived in. */
function bodyOf(error: unknown): any {
  if (error instanceof HttpErrorResponse) {
    return error.error ?? {};
  }
  return error ?? {};
}

export function chatErrorCode(error: unknown): ChatErrorCode | null {
  const code = bodyOf(error)?.error_code;
  return typeof code === 'string' && code.startsWith('CHAT_') ? (code as ChatErrorCode) : null;
}

export function chatErrorStatus(error: unknown): number | null {
  if (error instanceof HttpErrorResponse) {
    return error.status;
  }
  const status = bodyOf(error)?.status_code;
  return typeof status === 'number' ? status : null;
}

/**
 * How long the composer should stay shut after a 429.
 *
 * `Retry-After` is only readable on a GET (the only path that rethrows the response object) and
 * only when the API exposes the header through CORS — see the follow-ups doc. Everywhere else
 * this is the spec's flat 10 s.
 */
export function chatRetryAfterMs(error: unknown): number {
  if (error instanceof HttpErrorResponse) {
    const header = error.headers?.get?.('Retry-After');
    const seconds = Number(header);
    if (Number.isFinite(seconds) && seconds > 0) {
      return Math.min(seconds, 300) * 1000;
    }
  }
  const retryAfter = Number(bodyOf(error)?.retry_after);
  if (Number.isFinite(retryAfter) && retryAfter > 0) {
    return Math.min(retryAfter, 300) * 1000;
  }
  return CHAT_RATE_LIMIT_FALLBACK_MS;
}

/**
 * User-facing copy per error code.
 *
 * Deliberately plain: the server's own `message` is written for a developer reading a log, and
 * some of these ("not a member") need to explain a state change the user did not cause.
 */
const MESSAGES: Record<ChatErrorCode, string> = {
  CHAT_INVALID_REQUEST: 'That request could not be completed.',
  CHAT_UNAUTHENTICATED: 'Your session has expired.',
  CHAT_NOT_A_MEMBER: 'You are no longer a member of that conversation.',
  CHAT_NOT_GROUP_OWNER: 'Only the group owner can do that.',
  CHAT_NOT_SENDER: 'You can only edit or delete your own messages.',
  CHAT_OUT_OF_OFFICE: 'That person is not in your office.',
  CHAT_EXTERNAL_DISABLED: 'External users cannot be added to chat yet.',
  CHAT_EDIT_WINDOW_EXPIRED: 'Messages can only be edited for 15 minutes.',
  CHAT_ATTACHMENT_NOT_YOURS: 'That attachment could not be sent.',
  CHAT_NOT_FOUND: 'That conversation is no longer available.',
  CHAT_FILE_TOO_LARGE: 'That file is over the 25 MB limit.',
  CHAT_FILE_TYPE_BLOCKED: 'That file type is not allowed.',
  CHAT_EMPTY_MESSAGE: 'Type a message or attach a file first.',
  CHAT_MESSAGE_TOO_LONG: 'That message is too long.',
  CHAT_INVALID_STATUS: 'That status is not available.',
  CHAT_TOO_MANY_REACTIONS: 'This message already has the maximum number of reactions.',
  CHAT_RATE_LIMITED: 'You are sending messages too quickly — try again in a moment.',
  CHAT_SENDER_HIDDEN: 'You are unable to send messages right now.',
  CHAT_MASTER_ADMIN_REQUIRED: 'Only a Master Admin can do that.',
};

/** The message to toast, falling back to the caller's wording for anything unrecognised. */
export function chatErrorMessage(error: unknown, fallback: string): string {
  const code = chatErrorCode(error);
  return code ? MESSAGES[code] : fallback;
}

/**
 * True when the thread the user is looking at has gone: they were removed, it was deleted, or it
 * belongs to another office. Both codes mean "close it and re-read the sidebar" — the sidebar is
 * the only thing that can say what they still have access to.
 */
export function isChatGoneError(error: unknown): boolean {
  const code = chatErrorCode(error);
  return code === 'CHAT_NOT_A_MEMBER' || code === 'CHAT_NOT_FOUND';
}

export function isChatRateLimited(error: unknown): boolean {
  return chatErrorCode(error) === 'CHAT_RATE_LIMITED' || chatErrorStatus(error) === 429;
}

/**
 * True when a send was rejected because the sender has been hidden from chat.
 *
 * The client-side `amHidden` lock (driven by the realtime `user-hidden` push) should already have
 * caught this before the request went out — this is the belt to that braces, for a hide that
 * landed between the socket frame and the request, or a session that missed the frame entirely.
 */
export function isChatSenderHidden(error: unknown): boolean {
  return chatErrorCode(error) === 'CHAT_SENDER_HIDDEN';
}

/**
 * True when the failure says "there is no chat API here", as opposed to "the API said no".
 *
 * This is the whole test for putting the "Please refresh your browser" state on screen. A
 * network/CORS failure (status 0) or a route that does not exist qualifies; anything carrying a
 * `CHAT_*` code does not — that is a working backend answering, and the caller has real handling
 * for what it said.
 */
export function isChatApiUnavailable(error: unknown): boolean {
  if (chatErrorCode(error)) {
    return false;
  }
  const status = chatErrorStatus(error);
  if (status === null) {
    // A thrown Error (or a body with no status at all) — treat as unreachable, which is how a
    // missing endpoint surfaced before the module existed.
    return !(error instanceof HttpErrorResponse);
  }
  return status === 0 || status === 404 || status === 405 || status === 501 || status === 502 || status === 503 || status === 504;
}
